<template>
  <div class="lock-extension-landing-page">
    <!-- Loud, unmistakable loading state while a recommendation / file check is in flight. -->
    <div v-if="busyMessage" class="lx-loading-overlay">
      <div class="lx-loading-box">
        <i class="fa-duotone fa-spinner fa-spin lx-loading-spinner"></i>
        <div class="lx-loading-text">{{ busyMessage }}</div>
        <div class="lx-loading-sub">This can take a few moments — please wait.</div>
      </div>
    </div>

    <div class="page-shell">
      <div class="page-header">
        <div class="page-header-left">
          <div class="page-icon">
            <i class="fa-duotone fa-calendar-plus"></i>
          </div>

          <div>
            <h1 class="page-title">Lock Extension Request</h1>
            <p class="page-subtitle">
              Select a new expiration date and review the recommendation before submitting.
            </p>
          </div>
        </div>
      </div>

      <!-- Closed-window notice. Shown ONLY to requests the window actually blocks: a Best
           Effort loan is never told about a rule that doesn't apply to it. States the window
           itself in Eastern, plus the viewer's local equivalent; the disabled action button
           carries the "you can go again at…" time in the viewer's own clock. -->
      <div v-if="isLockedOutForThisRequest" class="lock-out-banner">
        <i class="fa-duotone fa-clock"></i>

        <div class="lock-out-banner-body">
          <div class="lock-out-banner-title">
            Extension requests are available between {{ lockOutUnlockEasternLabel }} and
            {{ lockOutRelockEasternLabel }} ET
          </div>

          <div v-if="requestWindowLocalLabel" class="lock-out-banner-text">
            That's {{ requestWindowLocalLabel }} your local time.
          </div>
        </div>
      </div>

      <div v-if="showSearch" class="loan-search-card">
        <div class="loan-search-icon">
          <i class="fa-duotone fa-magnifying-glass"></i>
        </div>
        <h2 class="loan-search-title">Find a Loan</h2>
        <p class="loan-search-subtitle">Enter a loan number to begin a lock extension request.</p>
        <div class="loan-search-row">
          <input v-model.trim="searchInput"
                 class="loan-search-input"
                 type="text"
                 placeholder="Loan number..."
                 :disabled="searchLoading"
                 @keyup.enter="searchByLoanNumber" />
          <button class="loan-search-btn"
                  :disabled="searchLoading || !searchInput"
                  @click="searchByLoanNumber">
            {{ searchLoading ? 'Searching...' : 'Find Loan' }}
          </button>
        </div>
        <div v-if="searchError" class="loan-search-error">{{ searchError }}</div>
      </div>

      <div v-if="!showSearch && loading" class="state-card">
        Loading lock extension request...
      </div>

      <div v-if="!showSearch && !loading && errorMessage" class="state-card error">
        {{ errorMessage }}
      </div>

      <template v-if="!showSearch && !loading && !errorMessage">
        <div class="content-grid">
          <div class="main-card">
            <!-- Loan/Borrower hero -->
            <div class="loan-hero">
              <div class="loan-hero-number">
                <span class="loan-hero-label">Loan</span>
                <span class="loan-hero-value">{{ loan.loan_number || loanId || '—' }}</span>
              </div>
              <div class="loan-hero-divider"></div>
              <div class="loan-hero-borrower">
                <span class="loan-hero-label">Borrower</span>
                <span class="loan-hero-value">{{ borrowerName }}</span>
              </div>
            </div>

            <!-- Top action bar: Current Expiration | New Date + Get Recommendation -->
            <div class="top-action-bar">
              <div class="top-action-expiration">
                <span class="top-action-label">Current Lock Expiration</span>
                <span class="top-action-date">{{ formatDate(currentLockExpiration) }}</span>
              </div>

              <div class="top-action-right">
                <div class="top-date-input-shell"
                     :class="{ disabled: submitting || actionLoading, 'needs-date-highlight': needsDateForLockDesk }"
                     @click="openDatePicker">
                  <label class="top-date-label">New Expiration Date</label>
                  <input ref="requestedExpirationDateInputTop"
                         v-model="selectedDate"
                         type="date"
                         class="date-input"
                         :min="minDate"
                         :disabled="submitting || actionLoading"
                         @change="onSelectedDateChanged" />
                </div>

                <button v-if="!shouldShowManualLockDeskEmailOption"
                        class="primary-action-btn primary-action-btn--top"
                        :disabled="submitting || actionLoading || checkingLoanFileLock || !canGetRecommendation"
                        @click="getRecommendation">
                  {{ primaryActionLabel }}
                </button>
              </div>
            </div>

            <div class="loan-summary">
              <div class="summary-row summary-row--highlight">
                <span class="summary-label">Market Based Extensions</span>
                <span class="summary-value">
                  {{ displayMarketBasedExtensionCount }} / {{ encompassEligibility.maxMarketBasedExtensions }}
                </span>
              </div>

        

              <div class="summary-row">
                <span class="summary-label">Channel</span>
                <span class="summary-value">{{ displayChannel(loan.channel) }}</span>
              </div>

              <div class="summary-row">
                <span class="summary-label">Investor</span>
                <span class="summary-value">{{ encompassEligibility.investor || '—' }}</span>
              </div>

              <div class="summary-row">
                <span class="summary-label">Lien Position</span>
                <span class="summary-value">{{ encompassEligibility.lienPosition || '—' }}</span>
              </div>

              <div class="summary-row">
                <span class="summary-label">Loan Officer</span>
                <span class="summary-value">{{ loan.loan_officer_name || loan.loan_officer_email || '—' }}</span>
              </div>

            </div>

            <div v-if="eligibilityBlockMessage"
                 :class="eligibilityMessageClass">
              {{ eligibilityBlockMessage }}
            </div>

            <!-- Loan file is locked in Encompass — must be closed before an extension can process. -->
            <div v-if="loanFileLockErrorMessage" class="loan-file-lock-banner">
              <i class="fa-duotone fa-lock"></i>
              <span>{{ loanFileLockErrorMessage }}</span>
            </div>

            <div v-if="shouldShowManualLockDeskEmailOption"
                 class="manual-lock-desk-detour-panel">
              <div class="manual-lock-desk-detour-title">
                Manual Lock Desk Processing Required
              </div>

              <div class="manual-lock-desk-detour-body">
                {{ manualLockDeskDetourMessage }}
              </div>

              <div class="manual-lock-desk-status">
                <span v-if="lockDeskQueueSubmitting">
                  <i class="fa-duotone fa-spinner fa-spin"></i> Sending to the Lock Desk...
                </span>
                <span v-else-if="lockDeskQueueItemCreated" class="manual-lock-desk-status--sent">
                  <i class="fa-duotone fa-check"></i> This request has been sent to the Lock Desk for review.
                </span>
                <span v-else-if="lockDeskQueueError" class="manual-lock-desk-status--error">
                  {{ lockDeskQueueError }}
                </span>
                <span v-else class="manual-lock-desk-status--action-needed">
                  <i class="fa-duotone fa-arrow-turn-up"></i>
                  Set the <strong>New Expiration Date</strong> above — that sends this loan to the Lock Desk for review.
                </span>
              </div>
            </div>

        
          </div>

          <div class="side-card">
            <h2 class="card-title">Guidelines</h2>

            <div class="guideline-list">
              <div class="guideline-item">
                <i class="fa-duotone fa-circle-check"></i>
                <span>Weekends are not valid expiration dates.</span>
              </div>

              <div class="guideline-item">
                <i class="fa-duotone fa-circle-check"></i>
                <span>Bank holidays are not valid expiration dates.</span>
              </div>

              <div class="guideline-item">
                <i class="fa-duotone fa-circle-check"></i>
                <span>The system creates a recommendation first. Nothing is submitted until you choose Submit.</span>
              </div>

              <div class="guideline-item">
                <i class="fa-duotone fa-circle-check"></i>
                <span>Only First Lien loans with investor Gold Star Mortgage Financial Group are eligible.</span>
              </div>

              <div class="guideline-item">
                <i class="fa-duotone fa-circle-check"></i>
                <span>Gold Star loans are limited to 60 days of extension. Requests over 60 days require manual review by the Lock Desk.</span>
              </div>

              <div class="guideline-item">
                <i class="fa-duotone fa-circle-check"></i>
                <span>Gold Star loans are limited to 2 Market Based Extensions per file.</span>
              </div>

              <div class="guideline-item">
                <i class="fa-duotone fa-circle-check"></i>
                <span>Gold Star loans can be extended at a cost of 2 basis points per day.</span>
              </div>

              <div class="guideline-item">
                <i class="fa-duotone fa-clock"></i>
                <span>
                  Requests are open {{ requestWindowEasternLabel }}, Monday through Friday.
                  Friday's opening stays open through the weekend.
                </span>
              </div>

              <div class="guideline-item">
                <i class="fa-duotone fa-globe"></i>
                <span>
                  All request times are Eastern Time.
                  <template v-if="requestWindowLocalLabel">
                    In your local time the window is {{ requestWindowLocalLabel }}.
                  </template>
                </span>
              </div>
            </div>
          </div>
        </div>

        <div v-if="recommendation && showRecommendationModal"
             class="lx-modal-backdrop"
             @click.self="closeRecommendationModal">
          <div class="recommendation-card lx-modal-card">
            <button class="lx-modal-close" aria-label="Close" @click="closeRecommendationModal">✕</button>

            <div class="card-header">
              <div>
                <h2 class="card-title">{{ extensionTypeLabel }}</h2>
                <p class="card-subtitle">Review the recommendation before submitting.</p>
              </div>

              <div class="modal-extension-summary" v-if="selectedDate && calculatedDays > 0">
                <div class="modal-extension-cost">
                  <span class="modal-extension-cost-label">Extension Cost</span>
                  <span class="modal-extension-cost-value">{{ estimatedExtensionCostBasisPoints }} Bps</span>
                </div>
                <div class="modal-updated-expiration">
                  Updated Expiration Date: {{ formatDate(selectedDate) }}
                </div>
              </div>
            </div>

          <div v-if="recommendationErrorMessage"
               class="recommendation-error-panel">
            <div class="recommendation-error-title">
              <i class="fa-duotone fa-triangle-exclamation"></i>
              {{ recommendationErrorTitle }}
            </div>

            <div class="recommendation-error-body">
              {{ recommendationErrorMessage }}
            </div>

            <div class="recommendation-error-help">
              {{ recommendationErrorHelpText }}
            </div>
          </div>

          <div v-else class="recommendation-summary">
            {{ recommendation.llm_summary || recommendation.decision_reason || 'No summary available.' }}
          </div>

          <div v-if="manualReviewQueueMessage" class="manual-review-queue-panel">
            <div class="manual-review-queue-title">
              Manual Review Required
            </div>

            <div class="manual-review-queue-body">
              {{ manualReviewQueueMessage }}
            </div>
          </div>

          <div v-if="!shouldHideMarketPriceNumbers" class="market-comparison-section">
            <div class="market-comparison-title">Market Comparison</div>

            <div class="result-grid">
              <div class="result-item">
                <span class="result-label">Current Market Net Price</span>
                <span class="result-value">
                  {{ formatPrice(currentMarketNetPrice) }}
                </span>
              </div>

              <div class="result-item">
                <span class="result-label">Current Locked Price</span>
                <span class="result-value">
                  {{ formatPrice(currentLockedPrice) }}
                </span>
              </div>
            </div>
          </div>

          <div v-if="actionErrorMessage" class="modal-action-error">
            {{ actionErrorMessage }}
          </div>

          <div v-if="actionSuccessMessage" class="modal-action-success">
            {{ actionSuccessMessage }}
          </div>

          <div v-if="shouldDetourPollySubmit"
               class="manual-lock-desk-detour-panel recommendation-detour-panel">
            <div class="manual-lock-desk-detour-title">
              Manual Lock Desk Processing Required
            </div>

            <div class="manual-lock-desk-detour-body">
              {{ manualLockDeskDetourMessage }}
            </div>

            <div class="manual-lock-desk-status">
              <span v-if="lockDeskQueueSubmitting">
                <i class="fa-duotone fa-spinner fa-spin"></i> Sending to the Lock Desk...
              </span>
              <span v-else-if="lockDeskQueueItemCreated" class="manual-lock-desk-status--sent">
                <i class="fa-duotone fa-check"></i> This request has been sent to the Lock Desk for review.
              </span>
              <span v-else-if="lockDeskQueueError" class="manual-lock-desk-status--error">
                {{ lockDeskQueueError }}
              </span>
              <span v-else class="manual-lock-desk-status--action-needed">
                <i class="fa-duotone fa-arrow-turn-up"></i>
                Set the <strong>New Expiration Date</strong> above — that sends this loan to the Lock Desk for review.
              </span>
            </div>
          </div>

          <div class="action-row">
            <!-- Hidden entirely when Lock Desk manual review is required — the request is
                 sent to the Lock Desk automatically (detour panel above) in that case. -->
            <button v-if="normalizedDecision !== 'manual_review' && !shouldDetourPollySubmit"
                    class="submit-btn"
                    :disabled="!canSubmitToPolly"
                    @click="submitToPolly">
              {{ actionLoading ? 'Submitting...' : 'Submit' }}
            </button>
          </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
  import api from '@/api/api.service'
  import auth from '@/authConfig.js'

  // Lock extension requests run on the Lock Desk's clock, not the user's: automated
  // requests unlock at 11:00 AM ET and relock at 3:00 AM ET, Monday–Friday. Saturday
  // and Sunday are never "M-F", so Friday's 11:00 AM opening carries straight through
  // the weekend and the next relock is 3:00 AM Monday.
  //
  // THE SERVER DECIDES. The browser clock is user-settable, so open/closed comes from
  // GetLockExtensionRequestWindow and the API enforces it again on submit
  // (GSWebAPIv2 Utility/LockExtensionRequestWindow.cs). The hours below are display
  // fallbacks for when the window call hasn't answered yet.
  const LOCK_OUT_TIME_ZONE = 'America/New_York'
  const LOCK_OUT_RELOCK_HOUR = 3
  const LOCK_OUT_UNLOCK_HOUR = 11

  // Re-asks the server for the window so a page left open flips state on its own at
  // 3:00 AM / 11:00 AM ET. setInterval is timer-based, so changing the OS clock
  // neither delays nor triggers it.
  const REQUEST_WINDOW_POLL_INTERVAL_MS = 60000

  // Must match LockExtensionRequestWindow.LandingPageSource in GSWebAPIv2 — it tells the
  // submit endpoint this came from the landing page (and so is window-gated), unlike the
  // Lock Desk's own dashboard.
  const LOCK_EXTENSION_REQUEST_SOURCE = 'extension_request_landing'

  export default {
    name: 'LockExtensionLanding',

    data() {
      return {
        temporaryManualLockDeskDetourEnabled: false,
        lockDeskManualProcessingEmail: 'lockdesk@goldstarfinancial.com',
        lockDeskQueueItemCreated: false,
        lockDeskQueueSubmitting: false,
        lockDeskQueueError: '',
        loading: false,
        submitting: false,
        actionLoading: false,

        requestSessionId: '',

        errorMessage: '',
        dateErrorMessage: '',
        actionErrorMessage: '',
        actionSuccessMessage: '',

        loanId: '',
        showSearch: !(
          this.$route?.query?.loan_id ||
          this.$route?.query?.loanId ||
          this.$route?.params?.loanId
        ),
        searchInput: '',
        searchLoading: false,
        searchError: '',
        loan: {},
        selectedDate: '',
        recommendation: null,
        // The recommendation now renders in a popup modal; opened when a recommendation arrives.
        showRecommendationModal: false,
        manualStatus: '',
        encompassEligibilityLoading: false,
        encompassEligibilityError: '',
        encompassEligibility: {
          investor: '',
          lienPosition: '',
          isAllowedInvestor: false,
          isAllowedLienPosition: false,
          isEligible: false,
          requiresManualReview: false,
          marketBasedExtensionCount: 0,
          maxMarketBasedExtensions: 2,
          maxAutomaticExtensionDays: 60,
          extensionCostBasisPointsPerDay: 2,
          extensionCostBasisPoints: 0,
          message: ''
        },
        loanFileLockStatus: {
          isLocked: false,
          lockedBy: '',
          message: ''
        },
        checkingLoanFileLock: false,
        loanFileLockErrorMessage: '',
        lockExtensionProcessingMessage: '',
        lockExtensionProcessingStatus: '',
        lockExtensionProcessingPoller: null,

        // Server-authoritative request window; null until the server answers, which the
        // page treats as open (the API still rejects an out-of-window request).
        requestWindow: null,
        requestWindowPollTimer: null,

        // Server time when known, otherwise local. Used only to resolve the viewer's own
        // time zone for display — never to decide whether the window is open.
        nowTick: new Date(),
      }
    },

    watch: {
      // Pop the recommendation modal open the moment a recommendation is produced.
      recommendation(val) {
        if (val) this.showRecommendationModal = true
      }
    },
    computed: {
      // Closed 3:00 AM – 11:00 AM ET, Monday through Friday — as decided by the SERVER,
      // never by the local clock. Fails open while the window is unknown (first load or
      // an unreachable API): the page stays usable and the API still rejects an
      // out-of-window request with its own message.
      isLockOutActive() {
        if (!this.requestWindow) return false

        return this.requestWindow.isOpen === false
      },

      unlockHourEastern() {
        return this.usableHour(this.requestWindow?.unlockHourEastern, LOCK_OUT_UNLOCK_HOUR)
      },

      relockHourEastern() {
        return this.usableHour(this.requestWindow?.relockHourEastern, LOCK_OUT_RELOCK_HOUR)
      },

      // Best Effort (non-Gold Star) requests are worked manually by the Lock Desk,
      // so the automated request window doesn't apply to them.
      isLockOutExempt() {
        return this.shouldDetourNonGoldStarExtension
      },

      isLockedOutForThisRequest() {
        return this.isLockOutActive && !this.isLockOutExempt
      },

      lockOutUnlockEasternLabel() {
        return this.formatHourLabel(this.unlockHourEastern)
      },

      lockOutRelockEasternLabel() {
        return this.formatHourLabel(this.relockHourEastern)
      },

      requestWindowEasternLabel() {
        return `${this.lockOutUnlockEasternLabel} – ${this.lockOutRelockEasternLabel} ET`
      },

      localTimeZoneLabel() {
        try {
          const parts = new Intl.DateTimeFormat(undefined, { timeZoneName: 'short' })
            .formatToParts(this.nowTick)

          return parts.find(p => p.type === 'timeZoneName')?.value || ''
        } catch {
          return ''
        }
      },

      // Compares the viewer's UTC offset to Eastern's, so Eastern-zone users other
      // than America/New_York (Detroit, Toronto, ...) are also treated as Eastern.
      isUserInEasternTime() {
        const easternOffsetMinutes = this.zoneOffsetMinutes(this.nowTick, LOCK_OUT_TIME_ZONE)

        if (easternOffsetMinutes === null) return true

        return easternOffsetMinutes === -this.nowTick.getTimezoneOffset()
      },

      // Converted from the actual unlock/relock instants the server returned, so the
      // conversion is DST-exact and needs no Eastern date math here. Empty for
      // Eastern-time users (nothing to convert) and until the server has answered.
      requestWindowLocalLabel() {
        if (this.isUserInEasternTime) return ''

        const opens = this.formatLocalTime(this.requestWindow?.nextUnlockAt)
        const closes = this.formatLocalTime(this.requestWindow?.nextRelockAt)

        if (!opens || !closes) return ''

        return `${opens} – ${closes}${this.localTimeZoneLabel ? ` ${this.localTimeZoneLabel}` : ''}`
      },

      nextUnlockLocalLabel() {
        const opens = this.formatLocalTime(this.requestWindow?.nextUnlockAt)

        if (!opens) return ''

        return `${opens}${this.localTimeZoneLabel ? ` ${this.localTimeZoneLabel}` : ''}`
      },

      // Empty for Eastern-time viewers — "11:00 AM ET" already tells them the time.
      localUnlockLabel() {
        return this.isUserInEasternTime ? '' : this.nextUnlockLocalLabel
      },

      // The unlock time in whichever clock the viewer actually reads: their own when they
      // are outside Eastern, otherwise Eastern.
      unlockLabelForViewer() {
        return this.localUnlockLabel || `${this.lockOutUnlockEasternLabel} ET`
      },

      lockOutActionErrorMessage() {
        const local = this.localUnlockLabel
          ? ` (${this.localUnlockLabel} your local time)`
          : ''

        return `Lock extension requests are closed until ${this.lockOutUnlockEasternLabel} ET${local}.`
      },

      isNonGoldStarExtension() {
        return (
          !!this.encompassEligibility.investor &&
          this.encompassEligibility.isAllowedInvestor !== true
        )
      },

      // temporaryManualLockDeskDetourEnabled now gates ONLY the market-based
      // detour (currently disabled). Non-Gold Star detour is standard behavior
      // and is intentionally independent of that flag.
      shouldDetourMarketBasedExtension() {
        return (
          this.temporaryManualLockDeskDetourEnabled === true &&
          !!this.recommendation &&
          this.isMarketBasedFreeExtensionRecommendation &&
          !this.isMarketBasedExtensionLimitReached
        )
      },

      shouldDetourNonGoldStarExtension() {
        return this.isNonGoldStarExtension
      },

      shouldDetourPollySubmit() {
        return (
          this.shouldDetourMarketBasedExtension ||
          this.shouldDetourNonGoldStarExtension
        )
      },

      shouldShowManualLockDeskEmailOption() {
        return (
          this.shouldDetourNonGoldStarExtension &&
          !this.recommendation
        )
      },

      // A usable requested extension date must be picked before we send a
      // non-Gold Star loan to the Lock Desk.
      hasValidRequestedDate() {
        return (
          !!this.selectedDate &&
          this.calculatedDays > 0 &&
          !this.dateErrorMessage
        )
      },

      // Drives the "pick a date" prompt + date-field highlight for non-GS loans.
      needsDateForLockDesk() {
        return (
          this.shouldShowManualLockDeskEmailOption &&
          !this.hasValidRequestedDate &&
          !this.lockDeskQueueItemCreated &&
          !this.lockDeskQueueSubmitting
        )
      },

      manualLockDeskDetourReason() {
        if (this.shouldDetourMarketBasedExtension) {
          return 'Market Based Extension'
        }

        if (this.shouldDetourNonGoldStarExtension) {
          return 'Non-Gold Star Extension'
        }

        return 'Manual Lock Extension Review'
      },

      manualLockDeskDetourMessage() {
        if (this.shouldDetourMarketBasedExtension) {
          return 'This appears to be a market based extension.'
        }

        if (this.shouldDetourNonGoldStarExtension) {
          return 'This loan is not eligible for automatic Gold Star lock extension processing.'
        }

        return 'This request should be sent to the Lock Desk for manual processing.'
      },
      displayMarketBasedExtensionCount() {
        const landingCount = this.getMarketBasedExtensionCountFromLoanData(this.loan)

        if (landingCount !== null) {
          return landingCount
        }

        return Number(this.encompassEligibility.marketBasedExtensionCount || 0)
      },
      shouldForceAtCostAutomaticSubmission() {
        return (
          this.isAllowedAtCostExtension &&
          this.isMarketBasedExtensionLimitReached
        )
      },
      isMarketBasedExtensionLimitReached() {
        const used = Number(this.encompassEligibility.marketBasedExtensionCount || 0)
        const max = Number(this.encompassEligibility.maxMarketBasedExtensions || 0)

        return max > 0 && used >= max
      },

      isAllowedAtCostExtension() {
        return (
          this.encompassEligibility.isAllowedInvestor === true &&
          this.encompassEligibility.isAllowedLienPosition === true &&
          this.isMarketBasedExtensionLimitReached
        )
      },

      isEligibleForRecommendationRequest() {
        return this.encompassEligibility.isEligible === true || this.isAllowedAtCostExtension
      },

      // Market- vs cost-based is decided by the ACTUAL pricing cost, not the summary text:
      // a market-based extension is FREE (0 bps); any positive at-cost bps is cost-based.
      // (Keying off text misfired on summaries like "does not qualify as a market-based free
      // extension" — the phrase mentions the term while denying it.)
      isMarketBasedFreeExtensionRecommendation() {
        return !!this.recommendation && Number(this.estimatedExtensionCostBasisPoints) === 0
      },
      // Modal title mirrors the same determination so the whole component agrees.
      extensionTypeLabel() {
        return this.isMarketBasedFreeExtensionRecommendation
          ? 'Market Based Extension'
          : 'Cost Based Extension'
      },

      shouldHideMarketPriceNumbers() {
        return (
          !!this.recommendation &&
          this.isMarketBasedFreeExtensionRecommendation &&
          !this.isMarketBasedExtensionLimitReached
        )
      },
      currentLockedPrice() {
        return (
          this.recommendation?.current_locked_price ??
          this.recommendation?.currentLockedPrice ??
          this.recommendation?.initial_price ??
          null
        )
      },
      manualReviewQueueMessage() {
        if (this.normalizedDecision !== 'manual_review') {
          return ''
        }

        return 'This request has been added to the manual review queue. Lock Desk review is required before this extension can proceed.'
      },
      estimatedExtensionCostBasisPoints() {
        if (this.recommendation) {
          const initial = Number(this.recommendation.initial_price)
          const final = Number(this.recommendation.final_price)

          if (Number.isFinite(initial) && Number.isFinite(final)) {
            return Math.max(0, Math.round((initial - final) * 100))
          }
        }

        return Number(this.encompassEligibility.extensionCostBasisPoints || 0)
      },
      currentMarketNetPrice() {
        return (
          this.recommendation?.current_market_net_price ??
          this.recommendation?.currentMarketNetPrice ??
          null
        )
      },

      lockExtensionPreviewFinalPrice() {
        return (
          this.recommendation?.lock_extension_preview_final_price ??
          this.recommendation?.lockExtensionPreviewFinalPrice ??
          this.recommendation?.final_price ??
          null
        )
      },

      hasCurrentMarketNetPrice() {
        if (
          this.currentMarketNetPrice === null ||
          this.currentMarketNetPrice === undefined ||
          this.currentMarketNetPrice === ''
        ) {
          return false
        }

        const value = Number(this.currentMarketNetPrice)
        return Number.isFinite(value)
      },
      recommendationErrorHelpText() {
        const raw =
          this.recommendation?.error ||
          this.recommendation?.error_message ||
          this.recommendation?.decision_reason ||
          this.recommendation?.llm_summary ||
          ''

        if (String(raw).toLowerCase().includes('unable to request - lock is expired')) {
          return 'Complete an official reprice in Polly, then return here and try the extension again. If the issue continues, send the file to the Lock Desk for manual review.'
        }

        return 'Change the requested date, reject the recommendation, or send the file to the Lock Desk for manual handling.'
      },
      recommendationErrorTitle() {
        const raw =
          this.recommendation?.error ||
          this.recommendation?.error_message ||
          this.recommendation?.decision_reason ||
          this.recommendation?.llm_summary ||
          ''

        if (String(raw).toLowerCase().includes('unable to request - lock is expired')) {
          return 'Polly requires a reprice first'
        }

        return 'Unable to submit automatically'
      },
      recommendationErrorMessage() {
        const raw =
          this.recommendation?.error ||
          this.recommendation?.error_message ||
          this.recommendation?.decision_reason ||
          this.recommendation?.llm_summary ||
          ''

        const normalized = String(raw).toLowerCase()

        if (
          normalized.includes('polly lock extension call failed') ||
          normalized.includes('unprocessable entity') ||
          normalized.includes('unable to request') ||
          normalized.includes('lock is expired')
        ) {
          return this.formatBackendErrorMessage(raw)
        }

        return ''
      },
      eligibilityMessageClass() {
        return this.encompassEligibility.requiresManualReview
          ? 'field-warning eligibility-error'
          : 'field-error eligibility-error'
      },
      // Non-empty while something is loading — drives the full-screen loading overlay.
      busyMessage() {
        if (this.checkingLoanFileLock) {
          return 'Checking loan file status…'
        }
        if (this.submitting) {
          return this.encompassEligibility.requiresManualReview && !this.shouldForceAtCostAutomaticSubmission
            ? 'Sending to the Lock Desk…'
            : 'Retrieving Extension Options…'
        }
        return ''
      },
      primaryActionLabel() {
        if (this.isLockedOutForThisRequest) {
          return `Closed Until ${this.unlockLabelForViewer}`
        }

        if (this.checkingLoanFileLock) {
          return 'Checking Loan File...'
        }

        if (this.submitting) {
          return this.encompassEligibility.requiresManualReview && !this.shouldForceAtCostAutomaticSubmission
            ? 'Sending to Lock Desk...'
            : 'Getting Recommendation...'
        }

        return this.encompassEligibility.requiresManualReview && !this.shouldForceAtCostAutomaticSubmission
          ? 'Send to Lock Desk for Review'
          : 'Get Recommendation'
      },
      borrowerName() {
        const fullName = this.loan.borrower_name || this.loan.borrowerName || ''
        if (fullName) return fullName

        const first = this.loan.borrower_first || this.loan.borrowerFirst || ''
        const last = this.loan.borrower_last || this.loan.borrowerLast || ''
        return `${first} ${last}`.trim() || '—'
      },

      currentLockExpiration() {
        return (
          this.loan.current_lock_expiration ||
          this.loan.currentLockExpiration ||
          this.loan.locked_expiration ||
          null
        )
      },

      minDate() {
        const base = this.currentLockExpiration
          ? this.parseAnyDate(this.currentLockExpiration)
          : new Date()

        if (!base) return ''

        base.setDate(base.getDate() + 1)
        return this.toDateInputValue(base)
      },

      calculatedDays() {
        if (!this.selectedDate || !this.currentLockExpiration) return 0

        const current = this.startOfDay(this.parseAnyDate(this.currentLockExpiration))
        const selected = this.startOfDay(this.parseDateInput(this.selectedDate))

        if (!current || !selected) return 0

        const diffMs = selected.getTime() - current.getTime()
        const days = Math.round(diffMs / 86400000)

        return days > 0 ? days : 0
      },

      isEligibleForLockExtension() {
        return this.isEligibleForRecommendationRequest
      },

      eligibilityBlockMessage() {
        if (this.encompassEligibilityLoading) {
          return 'Checking loan eligibility...'
        }

        if (this.encompassEligibilityError) {
          return this.encompassEligibilityError
        }

        if (!this.encompassEligibility.investor && !this.encompassEligibility.lienPosition) {
          return 'Unable to verify investor and lien position.'
        }

        if (!this.encompassEligibility.isEligible) {
          if (this.isAllowedAtCostExtension) {
            return ''
          }

          return this.encompassEligibility.message || 'This loan is not eligible for a lock extension recommendation.'
        }

        if (this.encompassEligibility.requiresManualReview && !this.shouldForceAtCostAutomaticSubmission) {
          return this.encompassEligibility.message || 'This request requires manual review by the Lock Desk.'
        }

        return ''
      },

      canGetRecommendation() {
        if (this.shouldShowManualLockDeskEmailOption) {
          return false
        }

        // Outside the request window nothing automated may start. The date field
        // stays usable so eligibility can still identify a Best Effort loan.
        if (this.isLockedOutForThisRequest) {
          return false
        }

        return (
          !!this.loanId &&
          !!this.selectedDate &&
          this.calculatedDays > 0 &&
          !this.dateErrorMessage &&
          this.isEligibleForLockExtension &&
          !this.encompassEligibilityLoading &&
          !this.loanFileLockStatus.isLocked
        )
      },

      normalizedDecision() {
        const raw =
          this.recommendation?.recommendation ??
          this.recommendation?.decision ??
          this.recommendation?.llm_decision ??
          ''

        const value = String(raw).trim().toLowerCase()

        if (
          this.shouldForceAtCostAutomaticSubmission &&
          (value === 'manual_review' || value === 'manual review' || value === 'review')
        ) {
          return 'approved'
        }

        if (value === 'approve' || value === 'approved') return 'approved'
        if (value === 'deny' || value === 'denied') return 'denied'
        if (value === 'manual_review' || value === 'manual review' || value === 'review') return 'manual_review'
        if (value === 'exception_needed' || value === 'exception needed' || value === 'exception') return 'exception_needed'
        if (!value) return 'no_recommendation'

        return value
      },

      canSubmitToPolly() {
        return (
          !this.isLockedOutForThisRequest &&
          !!this.recommendation &&
          this.normalizedDecision === 'approved' &&
          this.manualStatus !== 'submitted' &&
          this.manualStatus !== 'rejected_by_user' &&
          !this.actionLoading
        )
      },

      canRejectRecommendation() {
        return (
          !!this.recommendation &&
          this.manualStatus !== 'submitted' &&
          this.manualStatus !== 'rejected_by_user' &&
          !this.actionLoading
        )
      },

      currentAzureId() {
        return (
          localStorage.getItem('azure_id') ||
          localStorage.getItem('azureId') ||
          localStorage.getItem('active_user_azure_id') ||
          localStorage.getItem('user_azure_id') ||
          ''
        ).toString().trim().toLowerCase()
      },

      currentUserEmail() {
        // MSAL first. Nothing in this app ever writes an email to localStorage, so the keys below
        // are effectively always empty — which is why every submission from this page used to send
        // the literal "unknown-user" and that string ended up in the extension summaries. The MSAL
        // account's username IS the UPN/email.
        const account = auth.user?.()
        if (account?.username) return account.username.toString().trim()

        return (
          localStorage.getItem('email') ||
          localStorage.getItem('user_email') ||
          localStorage.getItem('active_user_email') ||
          localStorage.getItem('preferred_username') ||
          ''
        ).toString().trim()
      },

      // Same resolution order as LockDeskQueue.vue's currentUserName, so the name credited on
      // an auto-approved extension matches what the queue shows for work done by hand. No 'Me'
      // fallback — this goes to the server, which falls back to the email when it's blank.
      currentUserName() {
        const account = auth.user?.()
        if (account?.name) return account.name

        return (
          localStorage.getItem('full_name') ||
          localStorage.getItem('user_full_name') ||
          localStorage.getItem('display_name') ||
          localStorage.getItem('user_name') ||
          localStorage.getItem('name') ||
          ''
        ).toString().trim()
      }
    },

    async mounted() {
      this.loanId = (
        this.$route.query.loan_id ||
        this.$route.query.loanId ||
        this.$route.params.loanId ||
        ''
      ).toString().trim()

      this.requestSessionId =
        window.crypto?.randomUUID?.() ||
        `${Date.now()}-${Math.random().toString(16).slice(2)}`

      this.startRequestWindowPoll()

      if (!this.showSearch) {
        await this.loadLandingData()
      }
    },

    // Vue 3 hook name — `beforeDestroy` is never called here, so the timers below
    // would leak when leaving the page.
    beforeUnmount() {
      this.stopLockExtensionProcessingPoll()
      this.stopRequestWindowPoll()
    },

    methods: {
      // ── Anonymous access ──────────────────────────────────────────────────
      //  The lock expiration email links straight here with the loan id in the URL, and that link
      //  is the credential — no sign-in (business-accepted 2026-08-05). App.vue skips its forced
      //  login for this route, so there may be no MSAL account at all.
      //
      //  Everything below must therefore go through accessToken(), NOT auth.acquireTokenResponse():
      //  that call falls back to acquireTokenRedirect when there is no account, which would bounce
      //  an anonymous visitor to Entra mid-request. The API endpoints this page uses are
      //  [AllowAnonymous]; api.service omits the Authorization header when the token is empty.
      isAnonymousSession() {
        try {
          return !auth.user?.()
        } catch {
          return true
        }
      },

      // The bearer token when signed in, '' when not. Never triggers an interactive redirect.
      async accessToken() {
        if (this.isAnonymousSession()) return ''

        try {
          const tokenResponse = await auth.acquireTokenResponse()
          return tokenResponse?.accessToken || ''
        } catch {
          return ''
        }
      },

      startRequestWindowPoll() {
        this.stopRequestWindowPoll()

        // Deliberately not awaited — the banner can appear a moment after first paint.
        this.loadRequestWindow()

        this.requestWindowPollTimer = window.setInterval(() => {
          this.loadRequestWindow()
        }, REQUEST_WINDOW_POLL_INTERVAL_MS)
      },

      stopRequestWindowPoll() {
        if (this.requestWindowPollTimer) {
          window.clearInterval(this.requestWindowPollTimer)
          this.requestWindowPollTimer = null
        }
      },

      // Asks the server whether requests are open. On failure the last known window is
      // kept rather than falling back to the local clock, so a network blip can't be used
      // to unlock the page — and if the window was never known, the API is still the gate.
      async loadRequestWindow() {
        try {
          const token = await this.accessToken()

          const response = await api.GetLockExtensionRequestWindow(token)
          const data = response?.data?.data || response?.data || {}

          this.requestWindow = {
            // Anything other than an explicit false is treated as open, so an unexpected
            // payload shape can never lock every user out.
            isOpen: data.is_open !== false,
            unlockHourEastern: Number(data.unlock_hour_eastern),
            relockHourEastern: Number(data.relock_hour_eastern),
            nextUnlockAt: this.parseUtcInstant(data.next_unlock_utc),
            nextRelockAt: this.parseUtcInstant(data.next_relock_utc),
            message: data.message || ''
          }

          const serverNow = this.parseUtcInstant(data.utc_now)

          if (serverNow) {
            this.nowTick = serverNow
          }
        } catch {
          // Keep whatever the server last told us.
        }
      },

      parseUtcInstant(value) {
        if (!value) return null

        const parsed = new Date(value)

        return Number.isNaN(parsed.getTime()) ? null : parsed
      },

      // Wall-clock parts for an instant in a given time zone. Returns null when the zone
      // can't be resolved, so callers can skip the local-time conversion rather than
      // printing something wrong.
      getZonedParts(date, timeZone) {
        try {
          const formatter = new Intl.DateTimeFormat('en-US', {
            timeZone,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hourCycle: 'h23'
          })

          const values = {}
          formatter.formatToParts(date).forEach(part => {
            values[part.type] = part.value
          })

          const parts = {
            year: Number(values.year),
            month: Number(values.month),
            day: Number(values.day),
            hour: Number(values.hour),
            minute: Number(values.minute),
            second: Number(values.second)
          }

          const isUsable =
            Number.isFinite(parts.year) &&
            Number.isFinite(parts.month) &&
            Number.isFinite(parts.day) &&
            Number.isFinite(parts.hour) &&
            Number.isFinite(parts.minute)

          return isUsable ? parts : null
        } catch {
          return null
        }
      },

      // A zone's UTC offset in minutes at a given instant (DST aware).
      zoneOffsetMinutes(date, timeZone) {
        const parts = this.getZonedParts(date, timeZone)

        if (!parts) return null

        const wallClockAsUtc = Date.UTC(
          parts.year,
          parts.month - 1,
          parts.day,
          parts.hour,
          parts.minute,
          parts.second || 0
        )

        return Math.round((wallClockAsUtc - date.getTime()) / 60000)
      },

      formatLocalTime(date) {
        if (!date) return ''

        return date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
      },

      formatHourLabel(hour) {
        const suffix = hour >= 12 ? 'PM' : 'AM'
        const display = hour % 12 === 0 ? 12 : hour % 12

        return `${display}:00 ${suffix}`
      },

      // Only a whole hour of the day counts as usable — Number(null) is 0, which would
      // otherwise render a silent, wrong "12:00 AM" instead of the known fallback.
      usableHour(value, fallbackHour) {
        const hour = Number(value)

        return Number.isInteger(hour) && hour >= 0 && hour <= 23 ? hour : fallbackHour
      },

      closeRecommendationModal() {
        this.showRecommendationModal = false
      },
      async searchByLoanNumber() {
        if (!this.searchInput) return
        this.searchLoading = true
        this.searchError = ''
        try {
          const token = await this.accessToken()
          const res = await api.GetLoanDisplayData(this.searchInput, token)
          const guid = res?.data?.data?.loanId
          if (!guid) {
            this.searchError = 'Loan not found. Check the loan number and try again.'
            return
          }
          this.loanId = guid
          this.showSearch = false
          await this.loadLandingData()
        } catch (e) {
          this.searchError = e?.response?.data?.message || e?.message || 'Loan not found.'
        } finally {
          this.searchLoading = false
        }
      },

      // Auto-runs when a non-Gold Star extension is detected — no user action.
      // Creates a manual lock extension work item unless an open one already
      // exists for this loan, then reports that it went to the Lock Desk.
      async ensureLockDeskQueueItem() {
        if (
          this.lockDeskQueueItemCreated ||
          this.lockDeskQueueSubmitting ||
          !this.shouldDetourNonGoldStarExtension ||
          !this.hasValidRequestedDate ||
          !this.loanId
        ) {
          return
        }

        this.lockDeskQueueSubmitting = true
        this.lockDeskQueueError = ''

        try {
          const token = await this.accessToken()

          // Dedup: skip creation if an open lock_extension item already exists for this loan.
          // CreateLockDeskQueueItem does not dedup server-side, so we ask first.
          //
          // This deliberately asks a yes/no question rather than fetching the queue and filtering
          // here — this page is reachable anonymously from the emailed link, and the queue listing
          // carries every loan number, borrower and LO on the desk.
          const loanNumber = String(this.loan.loan_number || '').trim()
          let alreadyQueued = false

          if (loanNumber || this.loanId) {
            try {
              const existingResp = await api.HasOpenLockDeskQueueItem(
                loanNumber,
                this.loanId || null,
                'lock_extension',
                token
              )

              const payload = existingResp?.data
              alreadyQueued = (payload?.data ?? payload) === true
            } catch {
              // If the existence check fails, fall through and create the item.
            }
          }

          if (!alreadyQueued) {
            const requestedDays = this.calculatedDays || 0
            const requestDescription = this.selectedDate
              ? `${this.manualLockDeskDetourReason} — requested ${this.selectedDate} (${requestedDays} day${requestedDays === 1 ? '' : 's'})`
              : this.manualLockDeskDetourReason

            const response = await api.CreateLockDeskQueueItem(
              {
                request_type: 'lock_extension',
                loan_number: this.loan.loan_number || '',
                borrower_first: this.loan.borrower_first || this.loan.borrowerFirst || '',
                borrower_last: this.loan.borrower_last || this.loan.borrowerLast || '',
                loan_officer_name: this.loan.loan_officer_name || this.loan.loan_officer_email || '',
                orgid: this.loan.orgid || this.loan.org_id || '',
                channel: this.loan.channel || '',
                request_description: requestDescription,
                created_by_azure_id: this.currentAzureId
              },
              token
            )

            const result = response?.data?.data || response?.data || {}

            if (result.success === false) {
              throw new Error(result.error || result.message || 'Failed to add item to the Lock Desk Queue.')
            }
          }

          this.lockDeskQueueItemCreated = true
          this.manualStatus = 'sent_to_lock_desk'
        } catch (e) {
          this.lockDeskQueueError =
            e?.response?.data?.message ||
            e?.response?.data?.error ||
            e?.message ||
            'Failed to send this request to the Lock Desk.'
        } finally {
          this.lockDeskQueueSubmitting = false
        }
      },

      getMarketBasedExtensionCountFromLoanData(data) {
        if (!data) return null

        const value =
          data.marketBasedExtensionCount ??
          data.marketbasedextensioncount ??
          data.market_based_extension_count ??
          data.swift_market_based_extension_count ??
          data.swiftMarketBasedExtensionCount ??
          null

        if (value === null || value === undefined || value === '') {
          return null
        }

        const parsed = Number(value)

        return Number.isFinite(parsed) ? parsed : null
      },
      startLockExtensionProcessingPoll() {
        this.stopLockExtensionProcessingPoll()

        this.lockExtensionProcessingMessage = ''
        this.lockExtensionProcessingStatus = ''

        this.lockExtensionProcessingPoller = window.setInterval(async () => {
          await this.loadLockExtensionProcessingStatus()
        }, 5000)
      },

      stopLockExtensionProcessingPoll() {
        if (this.lockExtensionProcessingPoller) {
          window.clearInterval(this.lockExtensionProcessingPoller)
          this.lockExtensionProcessingPoller = null
        }
      },

      async loadLockExtensionProcessingStatus() {
        try {
          const token = await this.accessToken()

          const response = await api.GetLockExtensionProcessingStatus(
            this.loanId,
            token
          )

          const data = response?.data || {}

          const manualStatus = data.manualstatus || data.manualStatus || ''
          const message = data.message || ''
          const isProcessing =
            data.isprocessingstatus === true ||
            data.isProcessingStatus === true

          this.lockExtensionProcessingStatus = manualStatus

          if (isProcessing) {
            this.lockExtensionProcessingMessage =
              message ||
              'Polly is processing this request. The system is waiting and will retry automatically.'
          }
        } catch {
          // Do not block the main request if status polling fails.
        }
      },
      async checkLoanFileLockStatus() {
        this.checkingLoanFileLock = true
        this.loanFileLockErrorMessage = ''

        try {
          const token = await this.accessToken()

          const response = await api.GetLoanFileLockStatus(
            this.loanId,
            token
          )

          const data = response?.data || {}

          this.loanFileLockStatus = {
            isLocked: data.islocked === true || data.isLocked === true,
            lockedBy: data.lockedby || data.lockedBy || '',
            message: data.message || ''
          }

          if (this.loanFileLockStatus.isLocked) {
            this.loanFileLockErrorMessage =
              this.loanFileLockStatus.message ||
              `This loan file is currently locked${this.loanFileLockStatus.lockedBy ? ` by ${this.loanFileLockStatus.lockedBy}` : ''}. The file must be closed before the lock extension request can be processed.`

            return false
          }

          return true
        } catch (error) {
          this.loanFileLockErrorMessage =
            error?.response?.data?.message ||
            error?.message ||
            'Unable to verify whether the loan file is locked.'

          return false
        } finally {
          this.checkingLoanFileLock = false
        }
      },
      formatBackendErrorMessage(value) {
        const raw = String(value || '').trim()

        if (!raw) {
          return 'Unable to complete this request.'
        }

        if (raw.toLowerCase().includes('unable to request - lock is expired')) {
          return 'Polly could not process this extension because the lock appears expired or out of sync. Complete an official reprice in Polly, then try the lock extension again.'
        }

        if (raw.toLowerCase().includes('422 unprocessable entity')) {
          return 'Polly could not process this lock extension request.'
        }

        if (raw.toLowerCase().includes('polly lock extension call failed')) {
          return raw
            .replace(/^Polly lock extension call failed:\s*/i, '')
            .replace(/\|\s*\{.*$/i, '')
            .trim() || 'Polly could not process this lock extension request.'
        }

        return raw
      },
      async onSelectedDateChanged() {
        this.validateSelectedDate()

        if (!this.dateErrorMessage && this.selectedDate && this.calculatedDays > 0) {
          await this.loadLockExtensionEligibility()
        }
      },
      async loadLockExtensionEligibility() {
        if (!this.loanId) return

        const landingMarketBasedCount =
          this.getMarketBasedExtensionCountFromLoanData(this.loan)

        this.encompassEligibilityLoading = true
        this.encompassEligibilityError = ''
        this.encompassEligibility = {
          investor: '',
          lienPosition: '',
          isAllowedInvestor: false,
          isAllowedLienPosition: false,
          isEligible: false,
          requiresManualReview: false,
          marketBasedExtensionCount: landingMarketBasedCount ?? 0,
          maxMarketBasedExtensions: 2,
          maxAutomaticExtensionDays: 60,
          extensionCostBasisPointsPerDay: 2,
          extensionCostBasisPoints: 0,
          message: ''
        }

        try {
          const token = await this.accessToken()

          const response = await api.GetLockExtensionEligibility(
            this.loanId,
            this.calculatedDays || 0,
            token
          )

          const result = response?.data?.data || response?.data || {}

          this.encompassEligibility = {
            investor: result.investor || '',
            lienPosition: result.lienposition || '',
            isAllowedInvestor: result.isallowedinvestor === true,
            isAllowedLienPosition: result.isallowedlienposition === true,
            isEligible: result.iseligible === true,
            requiresManualReview: result.requiresmanualreview === true,
            marketBasedExtensionCount: Number(
              result.marketBasedExtensionCount ??
              result.marketbasedextensioncount ??
              result.market_based_extension_count ??
              landingMarketBasedCount ??
              0
            ),
            maxMarketBasedExtensions: Number(result.maxmarketbasedextensions || 2),
            maxAutomaticExtensionDays: Number(result.maxautomaticextensiondays || 60),
            extensionCostBasisPointsPerDay: Number(result.extensioncostbasispointsperday || 2),
            extensionCostBasisPoints: Number(result.extensioncostbasispoints || 0),
            message: result.message || ''
          }

          if (!this.encompassEligibility.isEligible && !this.isAllowedAtCostExtension) {
            this.encompassEligibilityError =
              this.encompassEligibility.message ||
              'This loan is not eligible for a lock extension recommendation.'
          }

          // Non-Gold Star loans can't be auto-processed — send them to the Lock
          // Desk automatically (no user action required, deduped).
          if (this.shouldDetourNonGoldStarExtension) {
            this.ensureLockDeskQueueItem()
          }
        } catch (e) {
          this.encompassEligibilityError =
            e?.response?.data?.message ||
            e?.message ||
            'Failed to verify investor and lien position.'
        } finally {
          this.encompassEligibilityLoading = false
        }
      },
      openDatePicker() {
        if (this.submitting || this.actionLoading) return

        const input = this.$refs.requestedExpirationDateInput
        if (!input) return

        if (typeof input.showPicker === 'function') {
          input.showPicker()
          return
        }

        input.focus()
        input.click()
      },
      parseDateInput(value) {
        if (!value) return null

        const parts = String(value).split('-').map(Number)
        if (parts.length !== 3) return null

        const [year, month, day] = parts
        if (!year || !month || !day) return null

        return new Date(year, month - 1, day, 12, 0, 0, 0)
      },

      parseAnyDate(value) {
        if (!value) return null

        if (typeof value === 'string') {
          const dateOnly = value.substring(0, 10)

          if (/^\d{4}-\d{2}-\d{2}$/.test(dateOnly)) {
            return this.parseDateInput(dateOnly)
          }
        }

        const date = new Date(value)
        if (Number.isNaN(date.getTime())) return null

        return new Date(
          date.getFullYear(),
          date.getMonth(),
          date.getDate(),
          12,
          0,
          0,
          0
        )
      },

      startOfDay(date) {
        if (!date) return null

        return new Date(
          date.getFullYear(),
          date.getMonth(),
          date.getDate(),
          12,
          0,
          0,
          0
        )
      },

      toDateInputValue(date) {
        const d = this.parseAnyDate(date)
        if (!d) return ''

        const year = d.getFullYear()
        const month = String(d.getMonth() + 1).padStart(2, '0')
        const day = String(d.getDate()).padStart(2, '0')

        return `${year}-${month}-${day}`
      },

      formatDate(value) {
        const date = this.parseAnyDate(value)
        if (!date) return '—'

        return date.toLocaleDateString('en-US')
      },
      async loadLandingData() {
        this.loading = true
        this.errorMessage = ''
        this.actionErrorMessage = ''
        this.actionSuccessMessage = ''

        // New loan — allow a fresh Lock Desk Queue item for it.
        this.lockDeskQueueItemCreated = false
        this.lockDeskQueueSubmitting = false
        this.lockDeskQueueError = ''

        try {
          const token = await this.accessToken()

          const response = await api.GetLockExtensionLandingData(
            this.loanId,
            token
          )

          this.loan = response?.data?.data || response?.data || {}

          const landingMarketBasedCount =
            this.getMarketBasedExtensionCountFromLoanData(this.loan)

          if (landingMarketBasedCount !== null) {
            this.encompassEligibility.marketBasedExtensionCount = landingMarketBasedCount
          }

          await this.loadLockExtensionEligibility()

          // Surface an Encompass file-lock up front (not only after Get Recommendation),
          // so the "loan is locked" notice is visible as soon as the page loads.
          await this.checkLoanFileLockStatus()
        } catch (e) {
          this.errorMessage =
            e?.response?.data?.message ||
            e?.message ||
            'Failed to load lock extension landing data.'
        } finally {
          this.loading = false
        }
      },

      validateSelectedDate() {
        this.dateErrorMessage = ''
        this.recommendation = null
        this.manualStatus = ''
        this.actionErrorMessage = ''
        this.actionSuccessMessage = ''

        if (!this.selectedDate) return

        const selected = this.startOfDay(this.parseDateInput(this.selectedDate))

        if (!selected || Number.isNaN(selected.getTime())) {
          this.dateErrorMessage = 'Select a valid date.'
          return
        }

        if (this.isWeekend(selected)) {
          this.dateErrorMessage = 'Selected date cannot fall on a weekend.'
          return
        }

        if (this.isBankHoliday(selected)) {
          this.dateErrorMessage = 'Selected date cannot fall on a bank holiday.'
          return
        }

        if (this.calculatedDays <= 0) {
          this.dateErrorMessage = 'Selected date must be after the current lock expiration date.'
        }
      },

      async getRecommendation() {
        this.validateSelectedDate()

        if (this.isLockedOutForThisRequest) {
          this.actionErrorMessage = this.lockOutActionErrorMessage
          return
        }

        if (!this.isEligibleForLockExtension) {
          this.actionErrorMessage =
            this.eligibilityBlockMessage ||
            'This loan is not eligible for a lock extension recommendation.'
          return
        }

        if (!this.canGetRecommendation) return

        this.submitting = true
        this.actionErrorMessage = ''
        this.actionSuccessMessage = ''
        this.lockExtensionProcessingMessage = ''
        this.lockExtensionProcessingStatus = ''

        const canContinue = await this.checkLoanFileLockStatus()

        if (!canContinue) {
          this.stopLockExtensionProcessingPoll()
          this.submitting = false
          return
        }

        try {
          const token = await this.accessToken()

          console.log('Lock extension date calc', {
            currentLockExpiration: this.currentLockExpiration,
            selectedDate: this.selectedDate,
            calculatedDays: this.calculatedDays
          })

          const payload = {
            loan_id: this.loanId,
            days_to_extend: this.calculatedDays,
            dry_run: false,
            request_session_id: this.requestSessionId
          }

          this.startLockExtensionProcessingPoll()

          const response = await api.RunSingleLockExtension(
            payload,
            token
          )

          const wrapper = response?.data || {}
          const result = wrapper?.data || wrapper || {}
          const resultDecision = String(
            result.recommendation ||
            result.decision ||
            result.llm_decision ||
            ''
          ).trim().toLowerCase()

          if (
            (result.requiresmanualreview === true || result.requiresManualReview === true) &&
            !this.shouldForceAtCostAutomaticSubmission
          ) {
            this.recommendation = null
            this.manualStatus = 'manual_review'
            this.actionSuccessMessage =
              result.message ||
              'The request has been sent to the Lock Desk for manual review.'
            return
          }

          if (
            (resultDecision === 'manual_review' || resultDecision === 'manual review') &&
            !this.shouldForceAtCostAutomaticSubmission
          ) {
            this.recommendation = {
              ...result,
              requested_days_to_extend: this.calculatedDays,
              effective_days_to_extend:
                result.effective_days_to_extend ||
                result.days_to_extend ||
                this.calculatedDays
            }

            this.manualStatus = 'manual_review'
            this.actionSuccessMessage =
              result.message ||
              'This request has been added to the manual review queue. Submit to Polly is disabled because Lock Desk review is required before this extension can proceed.'
            return
          }

          if (!result || (!result.decision && !result.llm_summary && !result.decision_reason)) {
            throw new Error(result.error || wrapper.message || 'Unable to create recommendation.')
          }

          const normalizedResult = this.shouldForceAtCostAutomaticSubmission
            ? {
              ...result,
              decision: 'approved',
              recommendation: 'approved',
              requiresmanualreview: false,
              requiresManualReview: false,
              decision_reason:
                result.decision_reason ||
                'Market-based extension limit has been reached. This request is eligible to proceed as an at-cost extension.',
              llm_summary:
                result.llm_summary ||
                `Loan #${this.loanId} is eligible to proceed as an at-cost lock extension because the market-based extension limit has been reached.`
            }
            : result

          this.recommendation = {
            ...normalizedResult,
            requested_days_to_extend: this.calculatedDays,
            effective_days_to_extend:
              normalizedResult.effective_days_to_extend ||
              normalizedResult.days_to_extend ||
              this.calculatedDays
          }

          this.manualStatus = ''
        } catch (e) {
          this.actionErrorMessage =
            e?.response?.data?.message ||
            e?.response?.data?.error ||
            e?.message ||
            'Failed to get recommendation.'
        } finally {
          this.stopLockExtensionProcessingPoll()
          this.submitting = false
        }
      },

      async submitToPolly() {
        if (this.isLockedOutForThisRequest) {
          this.actionErrorMessage = this.lockOutActionErrorMessage
          return
        }

        if (!this.canSubmitToPolly) return

        this.actionLoading = true
        this.actionErrorMessage = ''
        this.actionSuccessMessage = ''
        this.lockExtensionProcessingMessage = ''
        this.lockExtensionProcessingStatus = ''

        try {
          const token = await this.accessToken()

          const payload = {
            loan_id: this.loanId,
            days_to_extend:
              this.recommendation.effective_days_to_extend ||
              this.recommendation.days_to_extend ||
              this.calculatedDays,
            approved_by_azure_id: this.currentAzureId,
            // Deliberately NOT defaulted to "unknown-user" — that sentinel is what showed up in the
            // extension summaries. Left blank, the API fills it from the loan's own LO instead.
            approved_by_email: this.currentUserEmail,
            // Credited as the worker on the closed queue item the backend logs for an
            // auto-approved extension — the person who accepted it did the work.
            approved_by_name: this.currentUserName,
            dry_run: false,
            force: this.shouldForceAtCostAutomaticSubmission,
            // Tells the API this submission is window-gated, unlike the Lock Desk's
            // own dashboard which submits through the same endpoint.
            request_source: LOCK_EXTENSION_REQUEST_SOURCE
          }

          this.startLockExtensionProcessingPoll()

          const response = await api.SubmitApprovedLockExtension(
            payload,
            token
          )

          const result = response?.data?.data || response?.data || {}

          if (result.success === false) {
            throw new Error(result.error || result.message || 'Submit failed.')
          }

          this.manualStatus = 'submitted'
          this.actionSuccessMessage = result.message || 'Submitted to Polly.'
        } catch (e) {
          this.actionErrorMessage =
            e?.response?.data?.message ||
            e?.response?.data?.error ||
            e?.message ||
            'Failed to submit to Polly.'
        } finally {
          this.stopLockExtensionProcessingPoll()
          this.actionLoading = false
        }
      },

      async rejectRecommendation() {
        if (!this.canRejectRecommendation) return

        this.actionLoading = true
        this.actionErrorMessage = ''
        this.actionSuccessMessage = ''

        try {
          const token = await this.accessToken()

          const payload = {
            loan_id: this.loanId,
            rejected_by_azure_id: this.currentAzureId,
            rejected_by_email: this.currentUserEmail || 'unknown-user',
            reason: 'Rejected from lock extension request landing page.'
          }

          const response = await api.RejectLockExtensionRecommendation(
            payload,
            token
          )

          const result = response?.data?.data || response?.data || {}

          if (result.success === false) {
            throw new Error(result.error || result.message || 'Reject recommendation failed.')
          }

          this.manualStatus = 'rejected_by_user'
          this.actionSuccessMessage = result.message || 'Recommendation rejected.'
        } catch (e) {
          this.actionErrorMessage =
            e?.response?.data?.message ||
            e?.response?.data?.error ||
            e?.message ||
            'Failed to reject recommendation.'
        } finally {
          this.actionLoading = false
        }
      },

      changeDate() {
        this.recommendation = null
        this.manualStatus = ''
        this.actionErrorMessage = ''
        this.actionSuccessMessage = ''
      },

      decisionLabel(decision) {
        if (decision === 'approved') return 'Recommend Approval'
        if (decision === 'denied') return 'Recommend Denial'
        if (decision === 'manual_review') return 'Recommend Manual Review'
        if (decision === 'exception_needed') return 'Exception Needed'
        if (decision === 'no_recommendation') return 'No Recommendation Yet'
        return decision || '—'
      },

      decisionClass(decision) {
        if (decision === 'approved') return 'approve'
        if (decision === 'denied') return 'denied'
        if (decision === 'manual_review') return 'manual-review'
        if (decision === 'exception_needed') return 'exception-needed'
        return 'no-audit'
      },

      displayChannel(value) {
        if (!value) return '—'
        const normalized = String(value).trim().toLowerCase()
        if (normalized === 'banked - wholesale' || normalized === 'banked-wholesale' || normalized === 'banked wholesale') return 'Wholesale'
        if (normalized === 'banked - retail' || normalized === 'banked-retail' || normalized === 'banked retail') return 'Retail'
        return value
      },

      isWeekend(date) {
        return date.getDay() === 0 || date.getDay() === 6
      },

      isBankHoliday(date) {
        const holidays = this.getBankHolidays(date.getFullYear())
        return holidays.includes(this.toDateInputValue(date))
      },

      formatPrice(value) {
        if (value === null || value === undefined || value === '') {
          return '—'
        }

        const parsed = Number(value)

        if (!Number.isFinite(parsed)) {
          return '—'
        }

        return parsed.toFixed(3)
      },

      getBankHolidays(year) {
        const observed = (month, day) => {
          const date = new Date(year, month - 1, day)
          const dow = date.getDay()

          if (dow === 6) date.setDate(date.getDate() - 1)
          if (dow === 0) date.setDate(date.getDate() + 1)

          return this.toDateInputValue(date)
        }

        const nthWeekday = (month, weekday, occurrence) => {
          const date = new Date(year, month - 1, 1)

          while (date.getDay() !== weekday) {
            date.setDate(date.getDate() + 1)
          }

          date.setDate(date.getDate() + 7 * (occurrence - 1))
          return this.toDateInputValue(date)
        }

        const lastWeekday = (month, weekday) => {
          const date = new Date(year, month, 0)

          while (date.getDay() !== weekday) {
            date.setDate(date.getDate() - 1)
          }

          return this.toDateInputValue(date)
        }

        return [
          observed(1, 1),
          nthWeekday(1, 1, 3),
          nthWeekday(2, 1, 3),
          lastWeekday(5, 1),
          observed(6, 19),
          observed(7, 4),
          nthWeekday(9, 1, 1),
          nthWeekday(10, 1, 2),
          observed(11, 11),
          nthWeekday(11, 4, 4),
          observed(12, 25)
        ]
      }
    }
  }
</script>

<style scoped>

  /* ── Loud loading overlay ─────────────────────────────────────────────────── */
  .lx-loading-overlay {
    position: fixed;
    inset: 0;
    z-index: 1000;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(18, 28, 52, 0.55);
    backdrop-filter: blur(2px);
    animation: lx-overlay-in 0.15s ease-out;
  }

  @keyframes lx-overlay-in {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  .lx-loading-box {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    background: #ffffff;
    border-radius: 14px;
    padding: 30px 44px;
    box-shadow: 0 18px 50px rgba(15, 30, 70, 0.35);
    min-width: 260px;
  }

  .lx-loading-spinner {
    font-size: 46px;
    color: #2f6fed;
    --fa-secondary-opacity: 0.25;
  }

  .lx-loading-text {
    font-size: 16px;
    font-weight: 800;
    color: #1c2b4a;
    text-align: center;
  }

  .lx-loading-sub {
    font-size: 12.5px;
    color: #7b8397;
    text-align: center;
  }

  /* ── Recommendation popup modal ───────────────────────────────────────────── */
  .lx-modal-backdrop {
    position: fixed;
    inset: 0;
    z-index: 950;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    padding: 48px 16px;
    overflow-y: auto;
    background: rgba(18, 28, 52, 0.5);
    backdrop-filter: blur(2px);
    animation: lx-overlay-in 0.15s ease-out;
  }

  .lx-modal-card {
    position: relative;
    width: 100%;
    max-width: 720px;
    margin: auto;
    background: #ffffff;
    box-shadow: 0 22px 60px rgba(15, 30, 70, 0.35);
  }

  .lx-modal-close {
    position: absolute;
    top: 12px;
    right: 14px;
    border: none;
    background: transparent;
    font-size: 18px;
    line-height: 1;
    color: #9aa3b5;
    cursor: pointer;
    padding: 4px;
  }

  .lx-modal-close:hover { color: #d0342c; }

  /* Sits in the modal header's right slot (where the decision pill used to be),
     aligned with the title. Stacks the cost over the updated expiration date. */
  .modal-extension-summary {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 2px;
    text-align: right;
  }

  .modal-extension-cost {
    display: flex;
    align-items: baseline;
    gap: 10px;
    white-space: nowrap;
  }

  /* Same size/weight as the modal title per design. */
  .modal-extension-cost-label,
  .modal-extension-cost-value {
    font-size: 17px;
    font-weight: 800;
    color: #22314f;
  }

  .modal-updated-expiration {
    font-size: 13px;
    font-weight: 600;
    color: #7b8397;
    white-space: nowrap;
  }

  .manual-lock-desk-detour-panel {
    background: #fff7e7;
    border: 1px solid #f5e3b5;
    border-radius: 8px;
    padding: 12px 14px;
    color: #6f4b00;
    display: grid;
    gap: 10px;
  }

  .recommendation-detour-panel {
    margin-bottom: 14px;
  }

  .manual-lock-desk-detour-title {
    font-size: 13px;
    font-weight: 800;
  }

  .manual-lock-desk-detour-body {
    font-size: 13px;
    line-height: 1.55;
    font-weight: 700;
  }

  .manual-lock-desk-status {
    font-size: 13px;
    font-weight: 700;
    line-height: 1.5;
  }

    .manual-lock-desk-status--sent {
      color: #2f7a4d;
    }

    .manual-lock-desk-status--error {
      color: #a23b3b;
    }

    .manual-lock-desk-status--action-needed {
      display: inline-flex;
      align-items: center;
      gap: 7px;
      color: #8a4b00;
      background: #ffe8bf;
      border: 1px solid #f0c983;
      border-radius: 6px;
      padding: 8px 11px;
      font-weight: 800;
    }

  .top-date-input-shell.needs-date-highlight {
    border: 1px solid #e8973a;
    box-shadow: 0 0 0 3px rgba(232, 151, 58, 0.22);
    animation: lockDeskDatePulse 1.8s ease-in-out infinite;
  }

  @keyframes lockDeskDatePulse {
    0%, 100% { box-shadow: 0 0 0 3px rgba(232, 151, 58, 0.18); }
    50% { box-shadow: 0 0 0 5px rgba(232, 151, 58, 0.34); }
  }

  .lock-extension-landing-page {
    min-height: calc(100vh - 90px);
    height: calc(100vh - 90px);
    overflow-y: auto;
    overflow-x: hidden;
    background: #f7f8fb;
    color: #1f2a44;
    padding: 22px;
    box-sizing: border-box;
  }

  .page-shell {
    max-width: 1180px;
    margin: 0 auto;
    padding-bottom: 48px;
  }

  .hide-header .page-header {
    display: none;
  }

  .page-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 16px;
  }

  .page-header-left {
    display: flex;
    align-items: center;
    gap: 14px;
  }

  .page-icon {
    width: 48px;
    height: 48px;
    border-radius: 5px;
    background: #123a71;
    color: #ffffff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
  }

  .page-title {
    margin: 0;
    font-size: 22px;
    font-weight: 800;
    color: #1f2a44;
  }

  .page-subtitle {
    margin: 4px 0 0;
    font-size: 13px;
    color: #7b8397;
    font-weight: 500;
  }

  .content-grid {
    display: grid;
    grid-template-columns: minmax(0, 1.5fr) minmax(280px, 0.8fr);
    gap: 14px;
  }

  .main-card,
  .side-card,
  .recommendation-card,
  .state-card {
    background: #fbfbfd;
    border: 1px solid #e7ebf2;
    border-radius: 8px;
    padding: 18px;
  }

  .loan-search-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    padding: 56px 24px;
    gap: 10px;
  }

  .loan-search-icon {
    font-size: 40px;
    color: #b0b8cc;
    margin-bottom: 4px;
  }

  .loan-search-title {
    margin: 0;
    font-size: 20px;
    font-weight: 700;
    color: #1f2a44;
  }

  .loan-search-subtitle {
    margin: 0 0 8px;
    font-size: 14px;
    color: #7b8397;
  }

  .loan-search-row {
    display: flex;
    gap: 8px;
    width: 100%;
    max-width: 400px;
  }

  .loan-search-input {
    flex: 1;
    height: 40px;
    padding: 0 12px;
    font-size: 14px;
    border: 1px solid #d9dfeb;
    border-radius: 6px;
    outline: none;
    transition: border-color 0.15s ease;
  }

  .loan-search-input:focus {
    border-color: #0b84ff;
    box-shadow: 0 0 0 2px rgba(11, 132, 255, 0.12);
  }

  .loan-search-btn {
    height: 40px;
    padding: 0 20px;
    font-size: 14px;
    font-weight: 600;
    border: 1px solid #0b84ff;
    border-radius: 6px;
    background: #0b84ff;
    color: #ffffff;
    cursor: pointer;
    white-space: nowrap;
    transition: background 0.15s ease;
  }

  .loan-search-btn:hover { background: #0677e6; border-color: #0677e6; }
  .loan-search-btn:disabled { opacity: 0.55; cursor: not-allowed; }

  .loan-search-error {
    font-size: 13px;
    color: #b0332b;
    margin-top: 4px;
  }

  .card-header {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    align-items: flex-start;
    margin-bottom: 14px;
  }

  .card-title {
    margin: 0;
    font-size: 17px;
    font-weight: 800;
    color: #22314f;
    /* Neutralize the global (unscoped) .card-title leaking from dashboardCard.vue,
       which forces display:flex + justify-content:center and centers this title. */
    display: block;
    text-align: left;
    justify-content: flex-start;
    border-bottom: none;
    height: auto;
    padding: 0;
  }

  .card-subtitle {
    margin: 4px 0 0;
    font-size: 13px;
    color: #7b8397;
    text-align: left;
  }

  .loan-summary {
    display: grid;
    gap: 10px;
    margin-bottom: 18px;
  }

  .summary-row {
    display: flex;
    justify-content: space-between;
    gap: 14px;
    border-bottom: 1px solid #eef1f6;
    padding-bottom: 8px;
  }

  .summary-label,
  .result-label,
  .field-label {
    font-size: 12px;
    font-weight: 700;
    color: #7b8397;
  }

  .summary-value,
  .result-value {
    font-size: 13px;
    font-weight: 700;
    color: #24314f;
    text-align: right;
  }

  .date-form {
    display: grid;
    gap: 10px;
  }

  .date-input-shell {
    height: 40px;
    border: 1px solid #dfe4ee;
    background: #fcfcfe;
    border-radius: 5px;
    display: flex;
    align-items: center;
    cursor: pointer;
  }

    .date-input-shell.disabled {
      opacity: 0.65;
      cursor: not-allowed;
    }

  .date-input {
    width: 100%;
    height: 100%;
    border: 0;
    background: transparent;
    padding: 0 14px;
    font-size: 13px;
    color: #23314f;
    outline: none;
    cursor: pointer;
  }

    .date-input::-webkit-calendar-picker-indicator {
      cursor: pointer;
    }

    .date-input:disabled {
      cursor: not-allowed;
    }

  .field-error {
    color: #991b1b;
    background: #fee2e2;
    border: 1px solid #fecaca;
    border-radius: 6px;
    padding: 9px 10px;
    font-size: 12px;
    font-weight: 700;
  }

  .loan-file-lock-banner {
    display: flex;
    align-items: flex-start;
    gap: 9px;
    color: #92400e;
    background: #fef3c7;
    border: 1px solid #fcd34d;
    border-radius: 8px;
    padding: 12px 14px;
    font-size: 13px;
    font-weight: 600;
    line-height: 1.45;
    margin-top: 12px;
  }
  .loan-file-lock-banner .fa-duotone {
    color: #b45309;
    margin-top: 1px;
    flex-shrink: 0;
    font-size: 15px;
    --fa-primary-opacity: 1;
    --fa-secondary-opacity: 0.4;
  }

  /* Request-window notice — automated requests are closed 3:00–11:00 AM ET, M-F. */
  .lock-out-banner {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    background: #eef2fb;
    border: 1px solid #ccd8f2;
    border-radius: 8px;
    padding: 12px 14px;
    margin-bottom: 16px;
    color: #22314f;
  }

    .lock-out-banner .fa-duotone {
      color: #3b5bab;
      margin-top: 2px;
      flex-shrink: 0;
      font-size: 16px;
      --fa-primary-opacity: 1;
      --fa-secondary-opacity: 0.4;
    }

  .lock-out-banner-body {
    display: grid;
    gap: 6px;
  }

  .lock-out-banner-title {
    font-size: 13px;
    font-weight: 800;
  }

  .lock-out-banner-text {
    font-size: 13px;
    font-weight: 600;
    line-height: 1.5;
  }

  .field-help {
    color: #2c7a4b;
    background: #eef9f0;
    border: 1px solid #d9eedf;
    border-radius: 6px;
    padding: 9px 10px;
    font-size: 12px;
    font-weight: 700;
  }

  .primary-action-btn,
  .submit-btn,
  .reject-btn,
  .secondary-btn {
    border: 1px solid #d9dfeb;
    border-radius: 5px;
    height: 40px;
    padding: 0 14px;
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;
  }

  .primary-action-btn,
  .submit-btn {
    background: #eef9f0;
    border-color: #d9eedf;
    color: #2c7a4b;
  }

  .reject-btn {
    background: #fff7e7;
    border-color: #f5e3b5;
    color: #9a6b11;
  }

  .secondary-btn {
    background: #f5f8fc;
    border-color: #d9e2f0;
    color: #315db5;
  }

    .primary-action-btn:disabled,
    .submit-btn:disabled,
    .reject-btn:disabled,
    .secondary-btn:disabled {
      opacity: 0.55;
      cursor: not-allowed;
    }

  .guideline-list {
    display: grid;
    gap: 12px;
    margin-top: 14px;
  }

  .guideline-item {
    display: flex;
    align-items: flex-start;
    gap: 9px;
    color: #5f6b84;
    font-size: 13px;
    line-height: 1.45;
  }

    .guideline-item i {
      color: #5f79c7;
      margin-top: 2px;
    }

  .recommendation-card {
    margin-top: 14px;
  }

  .decision-pill {
    display: inline-flex;
    align-items: center;
    border-radius: 999px;
    padding: 5px 12px;
    font-size: 12px;
    font-weight: 800;
    white-space: nowrap;
    border: 1px solid transparent;
  }

    .decision-pill.approve {
      background: #eef9f0;
      border-color: #d9eedf;
      color: #2c7a4b;
    }

    .decision-pill.denied,
    .decision-pill.manual-review {
      background: #fff0f1;
      border-color: #f0d1d5;
      color: #b24553;
    }

    .decision-pill.exception-needed {
      background: #fff7e7;
      border-color: #f5e3b5;
      color: #9a6b11;
    }

    .decision-pill.no-audit {
      background: #f2f4f7;
      border-color: #e1e5ec;
      color: #6d7789;
    }

  .recommendation-summary {
    font-size: 13px;
    line-height: 1.65;
    color: #24314f;
    background: #f7f9fc;
    border: 1px solid #e6ebf2;
    border-radius: 6px;
    padding: 14px;
    margin-bottom: 14px;
  }

  .result-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 10px;
    margin-bottom: 14px;
  }

  .result-item {
    background: #ffffff;
    border: 1px solid #e7ebf2;
    border-radius: 6px;
    padding: 12px;
    display: grid;
    gap: 6px;
  }

  .action-row {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 10px;
  }

  .modal-action-error {
    margin-bottom: 12px;
    padding: 10px 12px;
    border-radius: 10px;
    background: #fee2e2;
    color: #991b1b;
    font-size: 13px;
    font-weight: 700;
  }

  .modal-action-success {
    margin-bottom: 12px;
    padding: 10px 12px;
    border-radius: 10px;
    background: #dcfce7;
    color: #166534;
    font-size: 13px;
    font-weight: 700;
  }

  .state-card {
    text-align: center;
    color: #7b8397;
  }

    .state-card.error {
      color: #991b1b;
    }

  @media (max-width: 900px) {
    .content-grid,
    .result-grid,
    .action-row {
      grid-template-columns: 1fr;
    }
  }

  .eligibility-error {
    margin-bottom: 12px;
  }

  .landing-action-message {
    margin-top: 2px;
    margin-bottom: 0;
  }

  .field-warning {
    color: #9a6b11;
    background: #fff7e7;
    border: 1px solid #f5e3b5;
    border-radius: 6px;
    padding: 9px 10px;
    font-size: 12px;
    font-weight: 700;
  }

  .recommendation-error-panel {
    background: #fff7e7;
    border: 1px solid #f5e3b5;
    border-radius: 8px;
    padding: 14px;
    margin-bottom: 14px;
    color: #6f4b00;
  }

  .recommendation-error-title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    font-weight: 800;
    margin-bottom: 6px;
  }

    .recommendation-error-title i {
      color: #9a6b11;
    }

  .recommendation-error-body {
    font-size: 13px;
    font-weight: 700;
    line-height: 1.5;
  }

  .recommendation-error-help {
    margin-top: 8px;
    font-size: 12px;
    line-height: 1.45;
    color: #7a5b1c;
  }

  .lock-extension-processing-message {
    margin-top: 10px;
    padding: 12px 14px;
    border: 1px solid #bfdbfe;
    background: #eff6ff;
    color: #1e3a8a;
    border-radius: 8px;
    font-size: 12px;
    line-height: 1.45;
  }

  .processing-title {
    font-weight: 800;
    margin-bottom: 4px;
  }

  .processing-body {
    font-weight: 600;
  }

  .market-comparison-section {
    margin-bottom: 14px;
  }

  .market-comparison-title {
    font-size: 12px;
    font-weight: 800;
    color: #7b8397;
    margin-bottom: 6px;
  }

  .manual-review-queue-panel {
    background: #fff7e7;
    border: 1px solid #f5e3b5;
    border-radius: 6px;
    padding: 12px 14px;
    margin-bottom: 14px;
    color: #6f4b00;
  }

  .manual-review-queue-title {
    font-size: 12px;
    font-weight: 800;
    margin-bottom: 6px;
  }

  .manual-review-queue-body {
    font-size: 13px;
    line-height: 1.55;
    font-weight: 700;
  }
  /* ── Loan Hero ─────────────────────────────────────────────── */
  .loan-hero {
    display: flex;
    align-items: center;
    gap: 0;
    background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%);
    border-radius: 12px;
    padding: 18px 24px;
    margin-bottom: 20px;
  }

  .loan-hero-number,
  .loan-hero-borrower {
    display: flex;
    flex-direction: column;
    gap: 3px;
    flex: 1;
  }

  .loan-hero-divider {
    width: 1px;
    height: 36px;
    background: rgba(255, 255, 255, 0.25);
    margin: 0 24px;
  }

  .loan-hero-label {
    font-size: 10px;
    font-weight: 900;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    color: rgba(255, 255, 255, 0.65);
  }

  .loan-hero-value {
    font-size: 20px;
    font-weight: 950;
    color: #fff;
    letter-spacing: 0.2px;
  }

  /* ── Top Action Bar ─────────────────────────────────────────── */
  .top-action-bar {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 20px;
    background: rgba(15, 23, 42, 0.03);
    border: 1px solid rgba(15, 23, 42, 0.08);
    border-radius: 12px;
    padding: 14px 18px;
    margin-bottom: 20px;
    flex-wrap: wrap;
  }

  .top-action-expiration {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .top-action-label {
    font-size: 11px;
    font-weight: 900;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: rgba(15, 23, 42, 0.5);
  }

  .top-action-date {
    font-size: 22px;
    font-weight: 950;
    color: #1e3a5f;
  }

  .top-action-right {
    display: flex;
    align-items: flex-end;
    gap: 12px;
    flex-wrap: wrap;
  }

  .top-date-input-shell {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 180px;
  }

  .top-date-input-shell .date-input {
    height: 40px;
    border-radius: 8px;
    padding: 0 12px;
    font-size: 14px;
    font-weight: 800;
    border: 1px solid rgba(15, 23, 42, 0.15);
    background: #fff;
    width: 100%;
    box-sizing: border-box;
  }

  .top-date-label {
    font-size: 11px;
    font-weight: 900;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: rgba(15, 23, 42, 0.5);
  }

  .primary-action-btn--top {
    height: 40px;
    margin-top: 0;
    align-self: flex-end;
  }

  /* ── Market Based Extension highlight ──────────────────────── */
  .summary-row--highlight {
    background: rgba(37, 99, 235, 0.05);
    border: 1px solid rgba(37, 99, 235, 0.15);
    border-radius: 8px;
    padding: 10px 14px;
    margin-bottom: 4px;
  }

  .summary-row--highlight .summary-label {
    font-weight: 950;
    color: #1d4ed8;
  }

  .summary-row--highlight .summary-value {
    font-weight: 950;
    font-size: 15px;
    color: #1d4ed8;
  }
</style>